import { refreshAccessToken } from './cognitoService';

// Joan web API (backend/web-api): device link and emergency contacts for the signed-in user
const API_URL = process.env.REACT_APP_JOAN_API_URL;

export interface EmergencyContact {
  name: string;
  phone: string;
  // false when the number has no SMS subscription, i.e. it would not receive alerts
  alerting: boolean;
}

// none: no device linked yet; shared: device is on more than one account, so contacts are locked
export type DeviceStatus = 'none' | 'linked' | 'shared';

export interface JoanProfile {
  email: string;
  name: string;
  phoneNumber: string;
  deviceId: string | null;
  deviceStatus: DeviceStatus;
  contacts: EmergencyContact[];
  maxContacts: number;
}

export class JoanApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'JoanApiError';
  }
}

const SESSION_EXPIRED = 'Your session has expired. Please sign in again.';

const request = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
  if (!API_URL) {
    throw new JoanApiError(0, 'not_configured', 'The Joan service is not configured (REACT_APP_JOAN_API_URL).');
  }

  const send = (token: string) => fetch(`${API_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' })
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  const token = localStorage.getItem('accessToken');
  if (!token) throw new JoanApiError(401, 'unauthorized', SESSION_EXPIRED);

  let response: Response;
  try {
    response = await send(token);
    if (response.status === 401) {
      const renewed = await refreshAccessToken();
      if (renewed) response = await send(renewed);
    }
  } catch {
    throw new JoanApiError(0, 'network', 'Could not reach the Joan service. Check your connection and try again.');
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new JoanApiError(
      response.status,
      data.error || 'error',
      response.status === 401 ? SESSION_EXPIRED : data.message || 'Something went wrong. Please try again.'
    );
  }
  return data as T;
};

// Contacts are addressed by their number; digits only keeps the URL free of '+'
const contactPath = (phone: string) => `/me/contacts/${phone.replace(/\D/g, '')}`;

export const getProfile = () => request<JoanProfile>('GET', '/me');

export const linkDevice = (deviceId: string) => request<JoanProfile>('PUT', '/me/device', { deviceId });

export const addContact = (name: string, phoneNumber: string) =>
  request<JoanProfile>('POST', '/me/contacts', { name, phoneNumber });

export const updateContact = (phone: string, name: string, phoneNumber: string) =>
  request<JoanProfile>('PUT', contactPath(phone), { name, phoneNumber });

export const removeContact = (phone: string) => request<JoanProfile>('DELETE', contactPath(phone));
